import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '../../common/Buttons/Button';
import sprite from '../../img/sprites.svg';

const plural = (n, one, few, many) => {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (last > 1 && last < 5) return few;
  if (last === 1) return one;

  return many;
};

const splitCountdown = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));

  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
};

/** Ілюстрація: навушники зі звуковими хвилями, які «збираються». */
const Illustration = () => (
  <svg className="vocabulary-soon__art" viewBox="0 0 240 180" aria-hidden="true">
    <defs>
      <linearGradient id="vocSoonBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#ede7ff" />
        <stop offset="1" stopColor="#f7f4ff" />
      </linearGradient>
      <linearGradient id="vocSoonViolet" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#8b6cf6" />
        <stop offset="1" stopColor="#6a48e6" />
      </linearGradient>
    </defs>
    <circle cx="120" cy="92" r="82" fill="url(#vocSoonBg)" />
    <circle className="vocabulary-soon__dot vocabulary-soon__dot--1" cx="42" cy="48" r="5" fill="#c9b8ff" />
    <circle className="vocabulary-soon__dot vocabulary-soon__dot--2" cx="204" cy="60" r="4" fill="#ffd166" />
    <circle className="vocabulary-soon__dot vocabulary-soon__dot--3" cx="188" cy="146" r="6" fill="#c9b8ff" />
    <circle className="vocabulary-soon__dot vocabulary-soon__dot--4" cx="52" cy="140" r="3.5" fill="#ffd166" />
    <path d="M70 108v-14c0-27.6 22.4-50 50-50s50 22.4 50 50v14" fill="none" stroke="url(#vocSoonViolet)" strokeWidth="9" strokeLinecap="round" />
    <rect x="58" y="100" width="26" height="40" rx="10" fill="url(#vocSoonViolet)" />
    <rect x="156" y="100" width="26" height="40" rx="10" fill="url(#vocSoonViolet)" />
    <rect x="66" y="108" width="10" height="24" rx="5" fill="#fff" opacity=".35" />
    <rect x="164" y="108" width="10" height="24" rx="5" fill="#fff" opacity=".35" />
    <g className="vocabulary-soon__waves" stroke="#8b6cf6" strokeWidth="5" strokeLinecap="round" fill="none">
      <path className="vocabulary-soon__wave" d="M104 110v20" />
      <path className="vocabulary-soon__wave" d="M114 100v40" />
      <path className="vocabulary-soon__wave" d="M124 106v28" />
      <path className="vocabulary-soon__wave" d="M134 112v16" />
    </g>
  </svg>
);

/**
 * Заставка сторінки лексики до моменту відкриття: таймер до `until` і
 * ледь помітна кнопка внизу праворуч → пароль → тимчасовий доступ.
 */
const VocabularyPreview = ({ until, onUnlock, onExpired }) => {
  const [left, setLeft] = useState(() => splitCountdown(new Date(until) - Date.now()));
  const [modalOpen, setModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const tick = () => {
      const ms = new Date(until) - Date.now();
      setLeft(splitCountdown(ms));
      if (ms <= 0) onExpired();
    };
    tick();
    const id = setInterval(tick, 1000);

    return () => clearInterval(id);
  }, [until, onExpired]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    inputRef.current?.focus();
    const onEsc = (e) => e.key === 'Escape' && setModalOpen(false);
    document.addEventListener('keydown', onEsc);

    return () => document.removeEventListener('keydown', onEsc);
  }, [modalOpen]);

  const closeModal = () => {
    setModalOpen(false);
    setPassword('');
    setError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim() || sending) return;

    setSending(true);
    setError('');
    try {
      await onUnlock(password);
      closeModal();
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 429
        ? 'Забагато спроб. Зачекайте хвилину.'
        : (err?.response?.data?.info?.message || 'Невірний пароль.'));
      setPassword('');
      inputRef.current?.focus();
    } finally {
      setSending(false);
    }
  };

  const cells = [
    [left.days, plural(left.days, 'день', 'дні', 'днів')],
    [left.hours, plural(left.hours, 'година', 'години', 'годин')],
    [left.minutes, plural(left.minutes, 'хвилина', 'хвилини', 'хвилин')],
    [left.seconds, plural(left.seconds, 'секунда', 'секунди', 'секунд')],
  ];

  return (
    <div className="vocabulary-soon">
      <Illustration />

      <h2 className="vocabulary-soon__title">Слова з озвучкою скоро будуть доступні</h2>
      <p className="vocabulary-soon__text">Буде доступно через</p>

      <div className="vocabulary-soon__timer" role="timer" aria-live="off">
        {cells.map(([value, label]) => (
          <div className="vocabulary-soon__cell" key={label}>
            <span className="vocabulary-soon__num">{String(value).padStart(2, '0')}</span>
            <span className="vocabulary-soon__label">{label}</span>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="vocabulary-soon__key"
        aria-label="Вхід для команди"
        onClick={() => setModalOpen(true)}
      />

      {modalOpen && (
        <div className="vocabulary-settings" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <form className="vocabulary-settings__card" role="dialog" aria-modal="true" aria-label="Пароль доступу" onSubmit={submit}>
            <div className="vocabulary-settings__head">
              <b>Пароль доступу</b>
              <button type="button" className="vocabulary-settings__close" aria-label="Закрити" onClick={closeModal}>
                <svg className="icon"><use href={`${sprite}#close`}></use></svg>
              </button>
            </div>

            <label className="vocabulary__field">
              <span>Введіть пароль — доступ до сторінки на 10 хвилин</span>
              <input
                ref={inputRef}
                id="vocabularyUnlockPassword"
                className={`vocabulary-soon__input ${error ? 'vocabulary-soon__input--error' : ''}`}
                type="password"
                autoComplete="off"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {error && <small className="vocabulary-soon__error">{error}</small>}
            </label>

            <div className="vocabulary-settings__foot">
              <Button buttonBg="green" loading={sending ? 'loading' : ''} type="submit" disabled={!password.trim() || sending}>
                Відкрити
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

VocabularyPreview.propTypes = {
  until: PropTypes.string.isRequired,
  onUnlock: PropTypes.func.isRequired,
  onExpired: PropTypes.func.isRequired,
};

export default VocabularyPreview;
